import Hls from 'hls.js'
import { useAtom } from 'jotai'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { VideoData } from '../../../interfaces/VideoOrStream'
import { videoElementAtom, videoSourceAtom, videoWrapperElementAtom } from './atoms'
import mux from 'mux-embed'
import { getTimeRemaining } from '../../../utils/time'
import useInterval from '../../../utils/useInterval'

//* ============================= Types ========================================

type VideoElementProps = {
  videoMetadata: VideoData
  scheduledTime: Date
  startMuted: boolean
  loop: boolean
}

//* ============================= React Component ==============================

const VideoElement = ({
  videoMetadata,
  startMuted,
  loop,
  scheduledTime,
}: VideoElementProps): JSX.Element => {
  // Make the video player DOM node accessible from within the component tree.
  const videoElementRef = useRef<HTMLVideoElement>(null)
  const videoWrapperElementRef = useRef<HTMLDivElement>(null)
  const [, setVideoElement] = useAtom(videoElementAtom)
  const [, setVideoWrapperElement] = useAtom(videoWrapperElementAtom)
  const [videoSource] = useAtom(videoSourceAtom)
  const trailerUri = `https://us.glassapi.xyz/${videoMetadata.trailer_uri}`
  const videoUri = `https://us.glassapi.xyz/${videoMetadata.video_uri}`
  const [live, setIsLive] = useState(scheduledTime <= new Date())

  useInterval(() => {
    const time = getTimeRemaining(scheduledTime)
    if (time.days <= 0 && time.hours <= 0 && time.minutes <= 0 && time.seconds <= 0) {
      setIsLive(true)
    } else {
      setIsLive(false)
    }
  }, 1000)

  useEffect(() => {
    if (!videoElementRef.current) {
      return
    }

    videoElementRef.current.muted = startMuted
  }, [videoElementRef.current])

  useEffect(() => {
    if (!videoElementRef.current) {
      return
    }

    mux.monitor(videoElementRef.current, {
      debug: true,
      data: {
        env_key:
          process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
            ? 'q29ju29mdtjk51crtmcsovfb9'
            : 'a4k04psq2r5pt8e3946tov31n',
        player_name: 'Glass Solana',
        player_init_time: Date.now(),
        video_title: videoMetadata.title,
        video_producer: videoMetadata.created_by,
      },
    })
  }, [videoElementRef.current, videoMetadata.title, videoMetadata.created_by])

  useEffect(() => {
    setVideoElement(videoElementRef.current)
  }, [setVideoElement])

  useEffect(() => {
    setVideoWrapperElement(videoWrapperElementRef.current)
  }, [setVideoWrapperElement])

  const posterSourceUri = `https://glassimg.imgix.net/${videoMetadata.thumbnail_uri}?ar=16:9&fit=crop&fm=webp&w=1200&h=675`

  // Set up the HLS integration.
  useEffect(() => {
    if (!videoElementRef.current) {
      return
    }
    const videoElement = videoElementRef.current
    const nextSrc = videoMetadata.gated
      ? videoMetadata.address !== videoSource?.address
        ? trailerUri
        : videoSource.src || trailerUri
      : live
      ? videoUri
      : trailerUri

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
      })

      hls.attachMedia(videoElement)

      // Load the primary source first.
      hls.on(Hls.Events.MEDIA_ATTACHED, async function () {
        hls.loadSource(nextSrc)
      })

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.log('HLS.Events.ERROR: ', event, data)
      })

      // Clean up HLS on unmount.
      return () => {
        hls.destroy()
      }
    } else if (videoElement.canPlayType('application/vnd.apple.mpegURL')) {
      // When the browser can play HLS natively, like Apple Safari, then
      // just pass the source.
      videoElement.src = nextSrc
      videoElement.play()
    }
  }, [
    videoSource,
    posterSourceUri,
    videoMetadata.gated,
    videoMetadata.address,
    trailerUri,
    videoUri,
    live,
  ])

  return (
    <div ref={videoWrapperElementRef}>
      <Video
        ref={videoElementRef}
        poster={posterSourceUri}
        playsInline
        autoPlay={startMuted}
        loop={loop}
      />
    </div>
  )
}

export default VideoElement

//* =========================== Styled Components ==============================

const Video = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`
